/**
 * Recognising "a piece of Valentine's code failed to load".
 *
 * When a lazily-imported chunk cannot be fetched, the browser says so in its
 * own words (#1425):
 *
 *   Chrome/Edge  Failed to fetch dynamically imported module: <url>
 *   Firefox      error loading dynamically imported module: <url>
 *   Safari       Importing a module script failed.
 *   Vite         Unable to preload CSS for <url> (VALENTINE-120)
 *
 * **What that does NOT tell us is why.** An earlier version of this module was
 * called `isStaleModuleError` and treated these messages as proof that a deploy
 * had removed the asset. It is not proof. The same message comes from a dropped
 * connection, an offline laptop, a proxy hiccup, or a CSP/MIME rejection. Acting
 * on the stronger claim meant hiding Retry from users whose problem was a
 * two-second network blip — the one case where Retry is exactly right.
 *
 * So the name and the contract are now the weaker, true one: the module did not
 * load, cause unknown. The UI offers both ways out and lets the user pick. Both
 * are cheap; guessing wrong is not.
 *
 * Valentine's master auto-deploys several times a day, and each deploy swaps the
 * asset tree, so a tab left open through one really can ask for a chunk that no
 * longer exists — that case is real, it is simply not the only one, and only a
 * reload fixes it because index.html is served `no-cache`.
 *
 * `ChunkErrorBoundary` in App.tsx covers the render-time case. It cannot cover
 * this one: an error boundary only sees throws during render or lifecycle, and
 * these surface inside async callbacks — pdf.js reporting through its `onError`,
 * a fetch `.catch`. Those paths have to ask the question themselves.
 */

/** What each engine or bundler says when a dynamic import or its asset cannot be fetched. */
const MODULE_LOAD_SIGNATURES = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'unable to preload css',
];

const MODULE_LOAD_URL_PATTERN = /(?:https?:\/\/|\/)[^\s"'<>]+/i;

// Says what reloading costs instead of promising it costs nothing. An earlier
// draft ended "your work is not affected either way", which is not something
// this code can guarantee: reloading discards every unsaved field on the page,
// and nothing here flushes or restores that. Telling a paralegal mid-task that
// a button is free when it is not is how work gets lost (ai-review, #1425).
export const MODULE_LOAD_NOTICE =
  'Part of Valentine could not load. Try again first — that is enough if the '
  + 'connection simply dropped. If it keeps failing, Valentine was probably '
  + 'updated while this page was open; reloading fixes that but will discard '
  + 'anything typed and not yet saved.';

/**
 * Whether this error means "a module failed to load", cause unknown.
 *
 * Reads nested causes and wrapper messages: pdf.js does not rethrow the import
 * failure, it reports `Setting up fake worker failed: "<original message>"`, so
 * matching only the top-level message would miss the exact error #1425 was filed
 * about.
 */
export function isModuleLoadError(error: unknown, depth = 0): boolean {
  if (error == null || depth > 4) return false;

  if (typeof error === 'string') return matches(error);

  if (typeof error === 'object') {
    const candidate = error as { message?: unknown; cause?: unknown };
    if (typeof candidate.message === 'string' && matches(candidate.message)) return true;
    // Guard the recursion: a cause chain can be cyclic, hence the depth cap.
    if (candidate.cause != null) return isModuleLoadError(candidate.cause, depth + 1);
  }

  return false;
}

/**
 * Return the lazy asset URL reported by the browser, if one is available.
 *
 * This deliberately does not claim the URL is stale. Callers that need to
 * distinguish a removed deployment asset from a transient module-load failure
 * must independently probe the same-origin URL before taking recovery action.
 */
export function getModuleLoadErrorUrl(error: unknown, depth = 0): string | null {
  if (error == null || depth > 4) return null;

  if (typeof error === 'string') return extractUrl(error);

  if (typeof error === 'object') {
    const candidate = error as { message?: unknown; cause?: unknown };
    if (typeof candidate.message === 'string') {
      const url = extractUrl(candidate.message);
      if (url) return url;
    }
    if (candidate.cause != null) return getModuleLoadErrorUrl(candidate.cause, depth + 1);
  }

  return null;
}

function matches(message: string): boolean {
  const normalized = message.toLowerCase();
  return MODULE_LOAD_SIGNATURES.some((signature) => normalized.includes(signature));
}

function extractUrl(message: string): string | null {
  const match = message.match(MODULE_LOAD_URL_PATTERN);
  if (!match) return null;
  const candidate = match[0].replace(/[.,;:!?]+$/, '');
  if (/^\/(?:html|plain|json|xml|javascript)$/i.test(candidate)) return null;
  return candidate;
}

/**
 * Reload the page.
 *
 * Takes the location as an injectable parameter so the reload ITSELF is
 * testable, not merely the fact that a component called something. Mocking this
 * function wholesale proves a button is wired to a seam and nothing about
 * whether the seam navigates -- an empty body would satisfy that test.
 *
 * Call it as `() => reloadForNewBuild()`. Passing it directly to `onClick`
 * would hand React's synthetic event in as `target`.
 */
export function reloadForNewBuild(target: Pick<Location, 'reload'> = window.location): void {
  target.reload();
}
